'use client';

import type { User } from '@supabase/supabase-js';
import { useEffect, useRef } from 'react';
import { getAnalyticsTracker } from '@/features/common/analytics/tracker';
import { Mixpanel } from '../mixpanel';
import { trackSignIn } from '../utils';

/**
 * @deprecated Phase 4: 신규 코드는 useUserIdentity 사용 권장
 * tiktok_sec_uid 우선 정책이 필요한 기존 코드에서만 사용
 *
 * Identifies the user in Mixpanel exactly once per user session on component load.
 * This hook is designed to be robust against SSR hydration issues, ensuring that
 * identify is called even if the user is already authenticated when the app loads.
 *
 * ⚠️ IMPORTANT:
 * - 신규 사용자(created_at === last_sign_in_at)는 useMixpanelAliasOnFirstSignup에서 처리
 * - 기존 사용자만 여기서 identify 처리
 * - trackSignIn은 Popow CRM과 이벤트 추적용으로만 호출 (Mixpanel identify는 별도 처리)
 *
 * 🎯 sec_uid 우선 정책:
 * - tiktok_sec_uid가 있으면 sec_uid를 distinct_id로 사용
 * - 없으면 email → user.id 순서로 fallback
 *
 * @param user The user object from Supabase auth.
 * @param tiktokSecUid The user's TikTok sec_uid from profile (optional).
 */
export function useIdentifyUserOnLoad(user: User | null, tiktokSecUid?: string | null) {
  const tracker = getAnalyticsTracker();
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user) {
      // 로그아웃 시 리셋
      if (identifiedUserIdRef.current) {
        identifiedUserIdRef.current = null;
      }
      return;
    }

    // 이미 식별된 사용자면 스킵
    if (user.id === identifiedUserIdRef.current) {
      return;
    }

    // 신규 사용자 체크 (created_at === last_sign_in_at)
    const createdAtStr = user.created_at;
    const lastSignInStr = user.last_sign_in_at ?? createdAtStr;
    const isNewUser = lastSignInStr === createdAtStr;

    // 🎯 신규 사용자는 useMixpanelAliasOnFirstSignup에서 처리하므로 여기서는 스킵
    if (isNewUser) {
      // trackSignIn은 Popow CRM과 이벤트 추적용으로만 호출
      trackSignIn(user, tracker, tiktokSecUid);
      identifiedUserIdRef.current = user.id;
      return;
    }

    // 🎯 기존 사용자: identify 호출 (alias는 이미 처리되었을 것)
    // sec_uid 우선 정책: tiktok_sec_uid → email → user.id
    const distinctId = tiktokSecUid || user.email || user.id;
    Mixpanel.identify(distinctId);

    // trackSignIn은 Popow CRM과 이벤트 추적용으로만 호출
    trackSignIn(user, tracker, tiktokSecUid);

    // Mark this user ID as identified for the current session
    identifiedUserIdRef.current = user.id;
  }, [user, tracker, tiktokSecUid]); // Re-run when user, tracker, or tiktokSecUid changes
}
